/**
 * Horizontal Slider Web Component
 * Custom element for a responsive slider with arrow navigation
 * Usage: <slider-horizontal items-desktop="4" items-large="3" items-tablet="2" items-mobile="2">
 */

class SliderHorizontal extends HTMLElement {
  constructor() {
    super();
    this.resizeTimer = null;
  }

  connectedCallback() {
    // Get child elements
    this.track = this.querySelector('[data-slider-track]');
    this.prevBtn = this.querySelector('[data-slider-prev]');
    this.nextBtn = this.querySelector('[data-slider-next]');

    if (!this.track) return;

    this.items = this.track.querySelectorAll('.featured-slider__item');

    if (!this.items.length) return;

    // Get responsive settings from attributes
    this.itemsDesktop = parseInt(this.getAttribute('items-desktop')) || 4;
    this.itemsLarge = parseInt(this.getAttribute('items-large')) || 3;
    this.itemsTablet = parseInt(this.getAttribute('items-tablet')) || 2;
    this.itemsMobile = parseInt(this.getAttribute('items-mobile')) || 2;

    // Get initial index from attribute (defaults to 0)
    this.currentIndex = parseInt(this.getAttribute('initial-index')) || 0;

    // Drag/swipe state
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.dragOffset = 0;
    this.hasMoved = false;

    // Bind methods
    this.handlePrev = this.handlePrev.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDragMove = this.handleDragMove.bind(this);
    this.handleDragEnd = this.handleDragEnd.bind(this);

    // Attach event listeners
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', this.handlePrev);
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', this.handleNext);
    }

    window.addEventListener('resize', this.handleResize);

    // Touch events
    this.track.addEventListener('touchstart', this.handleDragStart, { passive: true });
    this.track.addEventListener('touchmove', this.handleDragMove, { passive: false });
    this.track.addEventListener('touchend', this.handleDragEnd);

    // Mouse events
    this.track.addEventListener('mousedown', this.handleDragStart);
    document.addEventListener('mousemove', this.handleDragMove);
    document.addEventListener('mouseup', this.handleDragEnd);

    // Initial update
    this.updateSlider();
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.prevBtn) {
      this.prevBtn.removeEventListener('click', this.handlePrev);
    }

    if (this.nextBtn) {
      this.nextBtn.removeEventListener('click', this.handleNext);
    }

    window.removeEventListener('resize', this.handleResize);

    // Clean up drag events
    if (this.track) {
      this.track.removeEventListener('touchstart', this.handleDragStart);
      this.track.removeEventListener('touchmove', this.handleDragMove);
      this.track.removeEventListener('touchend', this.handleDragEnd);
      this.track.removeEventListener('mousedown', this.handleDragStart);
    }
    document.removeEventListener('mousemove', this.handleDragMove);
    document.removeEventListener('mouseup', this.handleDragEnd);

    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
  }

  /**
   * Determine how many items to show based on viewport width
   */
  getItemsToShow() {
    if (window.innerWidth >= 1280) return this.itemsDesktop;
    if (window.innerWidth > 989) return this.itemsLarge;
    if (window.innerWidth > 749) return this.itemsTablet;
    return this.itemsMobile;
  }

  /**
   * Update slider position and button states
   */
  updateSlider() {
    const itemsToShow = this.getItemsToShow();
    const maxIndex = Math.max(0, this.items.length - itemsToShow);

    // Ensure current index doesn't exceed max after resize
    if (this.currentIndex > maxIndex) {
      this.currentIndex = maxIndex;
    }

    // Calculate transform offset
    const itemWidth = this.items[0].offsetWidth;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    const offset = this.currentIndex * (itemWidth + gap);

    this.track.style.transform = `translateX(-${offset}px)`;

    // Update button states
    this.updateButtonStates(maxIndex);
  }

  /**
   * Update prev/next button states
   */
  updateButtonStates(maxIndex) {
    // Hide arrows if all items are visible
    const allItemsVisible = maxIndex === 0;

    if (this.prevBtn) {
      this.prevBtn.style.opacity = this.currentIndex === 0 ? '0.3' : '1';
      this.prevBtn.disabled = this.currentIndex === 0;
      this.prevBtn.style.display = allItemsVisible ? 'none' : 'flex';
    }

    if (this.nextBtn) {
      this.nextBtn.style.opacity = this.currentIndex >= maxIndex ? '0.3' : '1';
      this.nextBtn.disabled = this.currentIndex >= maxIndex;
      this.nextBtn.style.display = allItemsVisible ? 'none' : 'flex';
    }
  }

  /**
   * Navigate to previous slide
   */
  handlePrev(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    if (this.prevBtn && this.prevBtn.disabled) return;

    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateSlider();
      this.dispatchSlideChangeEvent();
    }
  }

  /**
   * Navigate to next slide
   */
  handleNext(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    if (this.nextBtn && this.nextBtn.disabled) return;

    const itemsToShow = this.getItemsToShow();
    const maxIndex = Math.max(0, this.items.length - itemsToShow);

    if (this.currentIndex < maxIndex) {
      this.currentIndex++;
      this.updateSlider();
      this.dispatchSlideChangeEvent();
    }
  }

  /**
   * Handle window resize with debounce
   */
  handleResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.updateSlider();
    }, 250);
  }

  /**
   * Handle drag/touch start
   */
  handleDragStart(e) {
    // Don't drag if all items visible
    const itemsToShow = this.getItemsToShow();
    if (this.items.length <= itemsToShow) return;

    this.isDragging = true;
    this.startX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    // Initialize currentX to startX so taps (no movement) have diff = 0
    this.currentX = this.startX;
    // Track if user actually moved during the drag
    this.hasMoved = false;

    // Calculate current offset
    const itemWidth = this.items[0].offsetWidth;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    this.dragOffset = this.currentIndex * (itemWidth + gap);

    // Disable transition during drag
    this.track.style.transition = 'none';

    // Prevent text selection during mouse drag
    if (e.type === 'mousedown') {
      e.preventDefault();
    }
  }

  /**
   * Handle drag/touch move
   */
  handleDragMove(e) {
    if (!this.isDragging) return;

    this.currentX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const diff = this.startX - this.currentX;

    // Mark that user has actually moved (not just tapped)
    if (Math.abs(diff) > 5) {
      this.hasMoved = true;
    }

    // Calculate bounds
    const itemWidth = this.items[0].offsetWidth;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    const itemsToShow = this.getItemsToShow();
    const maxOffset = (this.items.length - itemsToShow) * (itemWidth + gap);

    // Apply resistance at edges
    let newOffset = this.dragOffset + diff;
    if (newOffset < 0) {
      newOffset = newOffset * 0.3; // Resistance at start
    } else if (newOffset > maxOffset) {
      newOffset = maxOffset + (newOffset - maxOffset) * 0.3; // Resistance at end
    }

    this.track.style.transform = `translateX(-${newOffset}px)`;

    // Prevent vertical scroll while swiping horizontally
    if (e.type === 'touchmove' && Math.abs(diff) > 10) {
      e.preventDefault();
    }
  }

  /**
   * Handle drag/touch end
   */
  handleDragEnd() {
    if (!this.isDragging) return;

    this.isDragging = false;

    // Re-enable transition
    this.track.style.transition = 'transform 0.3s ease';

    // Only process slide change if user actually moved (not just tapped)
    if (!this.hasMoved) {
      // User just tapped - don't interfere with click events on links
      this.updateSlider();
      return;
    }

    const diff = this.startX - this.currentX;
    const threshold = 50; // Minimum drag distance to trigger slide change

    const itemsToShow = this.getItemsToShow();
    const maxIndex = Math.max(0, this.items.length - itemsToShow);

    if (Math.abs(diff) > threshold) {
      if (diff > 0 && this.currentIndex < maxIndex) {
        // Swiped left - go to next
        this.currentIndex++;
        this.dispatchSlideChangeEvent();
      } else if (diff < 0 && this.currentIndex > 0) {
        // Swiped right - go to prev
        this.currentIndex--;
        this.dispatchSlideChangeEvent();
      }
    }

    // Snap to current slide
    this.updateSlider();
  }

  /**
   * Dispatch custom event when slide changes
   */
  dispatchSlideChangeEvent() {
    this.dispatchEvent(new CustomEvent('slider-change', {
      detail: {
        currentIndex: this.currentIndex,
        itemsToShow: this.getItemsToShow(),
        totalItems: this.items.length
      },
      bubbles: true,
      composed: true
    }));
  }

  /**
   * Public method to go to specific slide
   */
  goToSlide(index) {
    const itemsToShow = this.getItemsToShow();
    const maxIndex = Math.max(0, this.items.length - itemsToShow);

    this.currentIndex = Math.max(0, Math.min(index, maxIndex));
    this.updateSlider();
    this.dispatchSlideChangeEvent();
  }

  /**
   * Public method to reset to first slide
   */
  reset() {
    this.currentIndex = 0;
    this.updateSlider();
  }
}

// Define custom element
if (!customElements.get('slider-horizontal')) {
  customElements.define('slider-horizontal', SliderHorizontal);
}

// Support Shopify theme editor
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const sliders = event.target.querySelectorAll('slider-horizontal');
    sliders.forEach(slider => {
      // Trigger re-initialization by calling connectedCallback logic
      if (slider.updateSlider) {
        slider.reset();
      }
    });
  });
}
